import { apiRequest } from '../../http';
import type { User } from '@shared/types';

const USERS_ENDPOINT = '/v1/users';
const EMAIL_VERIFICATION_ENDPOINT = `${USERS_ENDPOINT}/me/email-verification`;

export interface ListUsersResponse {
  data: User[];
  meta: {
    page: number;
    limit: number;
    total: number;
    totalPages: number;
  };
}

export function listUsers(params?: Record<string, unknown>, signal?: AbortSignal) {
  return apiRequest<ListUsersResponse>('get', USERS_ENDPOINT, undefined, { params, signal });
}

export function getProfile(uuid: string) {
  return apiRequest<User>('get', `${USERS_ENDPOINT}/${uuid}`);
}

export function updateProfile(uuid: string, data: Partial<User>) {
  return apiRequest<User>('patch', `${USERS_ENDPOINT}/${uuid}`, data);
}

/** Voice-recording consent state. `consent`: null = not yet asked, true = granted, false = declined. */
export interface RecordingConsent {
  consent: boolean | null;
  consentAt: string | null;
}

/** Read the user's one-time voice-recording consent. */
export function getRecordingConsent(uuid: string) {
  return apiRequest<RecordingConsent>('get', `${USERS_ENDPOINT}/${uuid}/recording-consent`);
}

/** Persist the user's one-time voice-recording consent decision. */
export function setRecordingConsent(uuid: string, granted: boolean) {
  return apiRequest<RecordingConsent>('put', `${USERS_ENDPOINT}/${uuid}/recording-consent`, {
    granted,
  });
}

export function uploadAvatar(uuid: string, file: File) {
  const formData = new FormData();
  formData.append('file', file);

  return apiRequest<User>('post', `${USERS_ENDPOINT}/${uuid}/avatar`, formData, {
    headers: {
      'Content-Type': 'multipart/form-data',
    },
  });
}

/** The caller's email verification state; instants are ISO-8601 UTC. */
export interface EmailVerificationStatus {
  email: string;
  emailVerified: boolean;
  emailVerifiedAt: string | null;
  pendingCode: {
    expiresAt: string;
    attemptsRemaining: number;
    resendAvailableAt: string;
  } | null;
}

export function getEmailVerification() {
  return apiRequest<EmailVerificationStatus>('get', EMAIL_VERIFICATION_ENDPOINT);
}

/** Mails a new code to the caller; earlier codes stop working. */
export function requestEmailVerificationCode() {
  return apiRequest<void>('post', `${EMAIL_VERIFICATION_ENDPOINT}/code`);
}

export function verifyEmailCode(code: string) {
  return apiRequest<{ emailVerified: true }>(
    'post',
    `${EMAIL_VERIFICATION_ENDPOINT}/verify`,
    { code }
  );
}
